import { NonAppNavWrapper } from '@/components/generic/non-app-nav'
import { TinyLoader } from '@/components/loaders'
import { Button } from '@/components/ui/button'
import { trpc } from '@/utils/trpc'
import { useUser } from '@clerk/nextjs'
import { sendGTMEvent } from '@next/third-parties/google'
import { X } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

// payment expired component

interface NameProps {
  firstName?: string | null
}

const PaymentExpired = ({ firstName }: NameProps) => {
  const { user: clerkUser } = useUser()

  return (
    <div className="bg-white p-6 mt-36 md:mx-auto">
      <X className="text-red-600 w-16 h-16 mx-auto my-6" />
      <div className="text-center">
        <h3 className="md:text-2xl text-base text-gray-900 font-semibold text-center">
          Payment Expired!
        </h3>
        <p className="text-gray-600 my-2">
          Sorry {clerkUser?.firstName}, your payment has expired. Please try again.
        </p>
        <div className="py-10 text-center">
          <Button className="px-12 font-semibold py-3">
            <Link href={`/pricing`}>Try Again</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

const PaymentIncomplete = ({ firstName }: NameProps) => {
  return (
    <div className="bg-white p-6 mt-36 md:mx-auto">
      <X className="text-red-600 w-16 h-16 mx-auto my-6" />
      <div className="text-center">
        <h3 className="md:text-2xl text-base text-gray-900 font-semibold text-center">
          Payment Incomplete!
        </h3>
        <p className="text-gray-600 my-2">
          Sorry {firstName}, your payment was not completed. Please try again.
        </p>
        <div className="py-10 text-center">
          <Button className="px-12 font-semibold py-3">
            <Link href={`/pricing`}>Try Again</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

const PaymentSuccess = ({ firstName }: NameProps) => {
  return (
    <div className="bg-white p-6 mt-36 md:mx-auto">
      <svg viewBox="0 0 24 24" className="text-green-600 w-16 h-16 mx-auto my-6">
        <path
          fill="currentColor"
          d="M12,0A12,12,0,1,0,24,12,12.014,12.014,0,0,0,12,0Zm6.927,8.2-6.845,9.289a1.011,1.011,0,0,1-1.43.188L5.764,13.769a1,1,0,1,1,1.25-1.562l4.076,3.261,6.227-8.451A1,1,0,1,1,18.927,8.2Z"
        ></path>
      </svg>
      <div className="text-center">
        <h3 className="md:text-2xl text-base text-gray-900 font-semibold text-center">
          Payment Done!
        </h3>
        <p className="text-gray-600 my-2">
          Thank you {firstName}, for completing your secure online payment.
        </p>
        <div className="py-10 text-center">
          <Button className="px-12 font-semibold py-3">
            <Link href={`/home`}>Get Started</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

const ErrorComp = () => {
  return (
    <div className="bg-white p-6 mt-36 md:mx-auto">
      <X className="text-red-600 w-16 h-16 mx-auto my-6" />
      <div className="text-center">
        <h3 className="md:text-2xl text-base text-gray-900 font-semibold text-center">
          Payment Error!
        </h3>
        <p className="text-gray-600 my-2">
          Sorry, there was an error processing your payment. Please try again.
        </p>
        <div className="py-10 text-center">
          <Button className="px-12 font-semibold py-3">
            <Link href={`/pricing`}>Try Again</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

function Payment() {
  const router = useRouter()
  const sessionId = router.query.sessionId
  const session = trpc.getSession.useQuery(sessionId as string, {
    enabled: Boolean(sessionId),
  })
  const { user: clerkUser, isLoaded } = useUser()

  useEffect(() => {
    if (!session.data) {
      return
    }

    if (session.data.session.status === 'complete') {
      sendGTMEvent({
        event: 'purchase',
        value: session.data.amount,
        currency: session.data.session.currency,
        transaction_id: session.data.session.id,
      })
    }
  }, [session.data?.session.status])

  const comp = () => {
    if (!isLoaded) {
      return <TinyLoader />
    }

    const name = clerkUser?.firstName

    switch (session.status) {
      case 'loading':
        return (
          <div className="w-full h-full justify-center items-center">
            <TinyLoader />
          </div>
        )
      case 'success':
        if (session.data.session.status === 'complete') {
          return <PaymentSuccess firstName={name} />
        } else if (session.data.session.status === 'open') {
          return <PaymentIncomplete firstName={name} />
        } else if (session.data.session.status === 'expired') {
          return <PaymentExpired firstName={name} />
        } else {
          return <ErrorComp />
        }
      case 'error':
        return <ErrorComp />
    }
  }

  return (
    <>
      <NonAppNavWrapper>
        <main
          className={`scroll-smooth flex min-h-screen  flex-col`} // ${inter.className}
        >
          {comp()}
        </main>
      </NonAppNavWrapper>
    </>
  )
}

export default Payment
